import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaTable, FaEye } from "react-icons/fa";
import { FiDownload, FiUpload } from "react-icons/fi";
import styles from "./BulkUpload.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomFileInput,
} from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { useSelector } from "react-redux";
import AxiosInstance, { TokenService } from "../../../../services/Api";

const BulkUpload = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const client_id = clientData?.client_id || "";
  const created_by = user?.id || "";
  const academic_year_id = clientData?.academic_year_id || "";

  // ─── Form State ───
  const [formData, setFormData] = useState({
    category: null,
    file: null,
    original_name: null,
  });

  // ─── Attendance upload state (stored after file select triggers /groupCockpit/upload) ───
  const [attendanceUploadData, setAttendanceUploadData] = useState({
    upload_path: "",
    original_name: "",
  });
  const [attendanceUploading, setAttendanceUploading] = useState(false);

  // ─── Table State ───
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);

  // ─── Upload State ───
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ─── PopupModal State ───
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });

  // ─── Category Options ───
  const categoryOptions = [
    { value: "Staff", label: translations.labels.staff || "Staff" },
    { value: "Student", label: translations.labels.student || "Student" },
    {
      value: "Attendance",
      label: translations.labels.attendance || "Attendance",
    },
  ];

  // ─── Helper to get category value string ───
  const getCategoryValue = () => {
    const cat = formData.category;
    if (!cat) return "";
    return typeof cat === "string"
      ? cat
      : cat?.value || formData.category || "";
  };

  // ─── Handlers ───
  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      file: null,
      original_name: null,
    }));

    // Reset attendance upload data when category changes
    setAttendanceUploadData({ upload_path: "", original_name: "" });
    setData([]);
    setSearchInput("");
    setApiSearch("");
    setPagination({ pageIndex: 0, pageSize: 10 });
    setTotalRows(0);
  };

  // ─── FILE CHANGE HANDLER ───
  const handleFileChange = async (file, name) => {
    if (!file) {
      setFormData((prev) => ({ ...prev, file: null, original_name: null }));
      setAttendanceUploadData({ upload_path: "", original_name: "" });
      return;
    }

    const category = getCategoryValue();
    if (category === "Staff" || category === "Student") {
      setFormData((prev) => ({
        ...prev,
        file: file,
        original_name: file.name,
      }));
    } else if (category === "Attendance") {
      setFormData((prev) => ({
        ...prev,
        file: file,
        original_name: file.name,
      }));

      setAttendanceUploading(true);
      try {
        const attendanceFormData = new FormData();
        attendanceFormData.append("attendance_xlsx", file);
        attendanceFormData.append("school_id", school_id);

        const uploadRes = await AxiosInstance.post(
          "/groupCockpit/upload",
          attendanceFormData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        const responseData =
          uploadRes.data?.[0] || uploadRes.data?.data?.[0] || {};
        const uploadPath = responseData.upload_path || "";
        const originalName = responseData.original_name || file.name;

        if (!uploadPath) {
          throw new Error("Upload path not received");
        }

        setAttendanceUploadData({
          upload_path: uploadPath,
          original_name: originalName,
        });

        setModalState({
          open: true,
          type: "success",
          message:
            translations.labels.fileUploadedReady ||
            "File uploaded successfully. Click 'UPLOAD FRAME SET' to proceed.",
          redirectPath: "",
        });
      } catch (error) {
        console.error("Attendance file upload error:", error);
        const errMsg =
          error?.response?.data?.message ||
          translations.labels.fileUploadFailed ||
          "Failed to upload file. Please try again.";
        setModalState({
          open: true,
          type: "failed",
          message: errMsg,
          redirectPath: "",
        });
        setFormData((prev) => ({ ...prev, file: null, original_name: null }));
        setAttendanceUploadData({ upload_path: "", original_name: "" });
      } finally {
        setAttendanceUploading(false);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        file: file,
        original_name: file.name,
      }));
    }
  };

  // ─── DOWNLOAD FRAME SET ───
  const handleDownloadFrameSet = async () => {
    const category = getCategoryValue();
    if (!category) {
      setModalState({
        open: true,
        type: "failed",
        message:
          translations.labels.selectCategoryFirst ||
          "Please select a category first",
        redirectPath: "",
      });
      return;
    }

    setDownloading(true);
    try {
      let downloadUrl = "";

      if (category === "Staff") {
        const response = await AxiosInstance.post("/school/staff/frame-sheet", {
          school_id,
        });
        downloadUrl = response.data?.data || "";
      } else if (category === "Student") {
        const response = await AxiosInstance.post(
          "/school/student/frame-sheet",
          { school_id },
        );
        downloadUrl = response.data?.file || "";
      } else if (category === "Attendance") {
        const response = await AxiosInstance.post(
          "/attendance/sample-download",
          { school_id },
        );
        downloadUrl = response.data?.data || response.data?.file || "";
      }

      // Relative path from the API → download from the CURRENT host.
      // Base is derived from the axios baseURL the API calls use
      // (http://localhost:8087/V1/api → http://localhost:8087,
      //  https://<tenant>/api/V1/api → https://<tenant>/api), so the
      // scheme/host/prefix always match a working backend. Absolute URLs
      // from an older backend keep working as-is.
      if (downloadUrl && !/^https?:\/\//i.test(downloadUrl)) {
        const apiBase = AxiosInstance.defaults.baseURL || "";
        const staticBase = apiBase.replace(/\/v1\/api\/?$/i, "");
        downloadUrl = `${staticBase.replace(/\/$/, "")}/${String(
          downloadUrl,
        ).replace(/^\//, "")}`;
      }

      if (downloadUrl) {
        // Fetch through AxiosInstance so the request carries the
        // Tenant-ID + auth headers (a plain <a href> navigation sends
        // no headers), then save the blob locally with the real
        // filename. Existing backend/middleware stays untouched.
        const fileName = downloadUrl.split("/").pop() || "Frame_Set.xlsx";
        const fileRes = await AxiosInstance.get(downloadUrl, {
          responseType: "blob",
        });
        const objectUrl = URL.createObjectURL(
          new Blob([fileRes.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } else {
        setModalState({
          open: true,
          type: "failed",
          message:
            translations.labels.downloadFailed ||
            "Failed to download. Please try again.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Download frame set error:", error);
      setModalState({
        open: true,
        type: "failed",
        message:
          translations.labels.downloadFailed ||
          "Failed to download. Please try again.",
        redirectPath: "",
      });
    } finally {
      setDownloading(false);
    }
  };

  // ─── UPLOAD FRAME SET (button click) ───
  const handleUploadFrameSet = async () => {
    const category = getCategoryValue();

    if (!category) {
      setModalState({
        open: true,
        type: "failed",
        message:
          translations.labels.selectCategoryFirst ||
          "Please select a category first",
        redirectPath: "",
      });
      return;
    }

    if ((category === "Staff" || category === "Student") && !formData.file) {
      setModalState({
        open: true,
        type: "failed",
        message:
          translations.labels.pleaseSelectExcelFile ||
          "Please select an Excel file to upload.",
        redirectPath: "",
      });
      return;
    }

    if (category === "Attendance" && !attendanceUploadData.upload_path) {
      setModalState({
        open: true,
        type: "failed",
        message:
          translations.labels.pleaseSelectExcelFile ||
          "Please select an Excel file to upload.",
        redirectPath: "",
      });
      return;
    }

    setUploading(true);
    try {
      let response;

      if (category === "Staff") {
        const staffFormData = new FormData();
        staffFormData.append("staff_path", formData.file);
        staffFormData.append("school_id", school_id);
        staffFormData.append("client_id", client_id);
        staffFormData.append("created_by", created_by);

        response = await AxiosInstance.post(
          "/school/staff/bulk-upload",
          staffFormData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } else if (category === "Student") {
        const studentFormData = new FormData();
        studentFormData.append("student_path", formData.file);
        studentFormData.append("school_id", school_id);
        studentFormData.append("client_id", client_id);
        studentFormData.append("created_by", created_by);
        studentFormData.append("academic_year", academic_year_id);

        response = await AxiosInstance.post(
          "/school/student/bulk-upload",
          studentFormData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } else if (category === "Attendance") {
        response = await AxiosInstance.post("/attendance/bulk-upload", {
          upload_path: attendanceUploadData.upload_path,
          original_name: attendanceUploadData.original_name,
          school_id,
          client_id,
          created_by,
        });
      }

      const { status, message, summary } = response?.data || {};

      if (status) {
        const successMsg = summary
          ? `${message} (Total: ${summary.totalRows}, Success: ${summary.successCount}, Failed: ${summary.failureCount})`
          : message ||
            translations.labels.bulkUploadSuccess ||
            "File uploaded successfully!";

        setModalState({
          open: true,
          type: "success",
          message: successMsg,
          redirectPath: "",
        });

        if (category === "Staff") fetchStaffList();
        if (category === "Student") fetchStudentList();
      } else {
        const failMsg = summary
          ? `${message} (Total: ${summary.totalRows}, Success: ${summary.successCount}, Failed: ${summary.failureCount})`
          : message ||
            translations.labels.bulkUploadFailed ||
            "Failed to upload file. Please try again.";

        setModalState({
          open: true,
          type: "failed",
          message: failMsg,
          redirectPath: "",
        });
      }

      setFormData((prev) => ({ ...prev, file: null, original_name: null }));
      setAttendanceUploadData({ upload_path: "", original_name: "" });
    } catch (error) {
      console.error("Upload frame set error:", error);

      const resData = error?.response?.data;
      const errMsg = resData?.summary
        ? `${resData.message} (Total: ${resData.summary.totalRows}, Success: ${resData.summary.successCount}, Failed: ${resData.summary.failureCount})`
        : resData?.message ||
          translations.labels.bulkUploadFailed ||
          "Failed to upload file. Please try again.";

      setModalState({
        open: true,
        type: "failed",
        message: errMsg,
        redirectPath: "",
      });
    } finally {
      setUploading(false);
      setFormData((prev) => ({
        category: null,
        file: null,
        original_name: null,
      }));
      setAttendanceUploadData({ upload_path: "", original_name: "" });
      setSearchInput("");
      setApiSearch("");
      setPagination({ pageIndex: 0, pageSize: 10 });
      setTotalRows(0);
    }
  };

  // ─── LIST FETCHERS ───
  const fetchStaffList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/school/staff/list", {
        id: school_id,
        search: apiSearch,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });
      const resData = response.data || {};
      setData(resData.data || []);
      setTotalRows(resData.pagination?.total || 0);
    } catch (error) {
      console.error("Staff list error:", error);
      setData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [school_id, apiSearch, pagination.pageIndex, pagination.pageSize]);

  const fetchStudentList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/school/studentsList", {
        school_id,
        student_class: "",
        section: "",
        gender: "",
        search: apiSearch,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        academic_year_id,
      });
      const resData = response.data || {};
      setData(resData.data || []);
      setTotalRows(resData.pagination?.total || 0);
    } catch (error) {
      console.error("Student list error:", error);
      setData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [
    school_id,
    apiSearch,
    pagination.pageIndex,
    pagination.pageSize,
    academic_year_id,
  ]);

  // ─── Fetch list on category / pagination / search change ───
  useEffect(() => {
    const category = getCategoryValue();
    if (!category) return;

    if (category === "Staff") {
      fetchStaffList();
    } else if (category === "Student") {
      fetchStudentList();
    } else if (category === "Attendance") {
      setData([]);
      setTotalRows(0);
    }
  }, [
    formData.category,
    fetchStaffList,
    fetchStudentList,
    pagination.pageIndex,
    pagination.pageSize,
    apiSearch,
  ]);

  // ─── Handle View ───
  const handleView = (row) => {
    if (formData?.category?.value === "Staff") {
      navigate("/user/staffSetup", {
        state: { staffId: row.id, staffData: row },
      });
    } else if (formData?.category?.value === "Student") {
      navigate("/user/studentCreate", {
        state: { ID: row.stud_id, studentData: row, mode: "view" },
      });
    }
  };

  // ─── COLUMNS ───
  const staffColumns = useMemo(
    () => [
      {
        accessorKey: "staff_id",
        header: translations.labels.empId || "Emp ID",
        size: 100,
      },
      {
        accessorKey: "name",
        header: translations.labels.name || "Name",
        size: 150,
      },
      {
        accessorKey: "email",
        header: translations.labels.email || "Email",
        size: 200,
      },
      {
        accessorKey: "staff_type",
        header: translations.labels.staffType || "Staff Type",
        size: 120,
      },
      {
        accessorKey: "mobile_no",
        header: translations.labels.mobileNo || "Mobile No",
        size: 130,
      },
      {
        accessorKey: "gender",
        header: translations.labels.gender || "Gender",
        size: 100,
      },
      {
        accessorKey: "subject_name",
        header: translations.labels.courses || "Courses",
        size: 150,
      },
      {
        accessorKey: "action",
        header: translations.labels.actions || "Actions",
        size: 80,
        Cell: ({ row }) => (
          <div className={styles.actionButtons}>
            <button
              className={styles.viewButton}
              onClick={() => handleView(row.original)}
              title={translations.labels.view || "View"}
            >
              <FaEye />
            </button>
          </div>
        ),
      },
    ],
    [translations],
  );

  const studentColumns = useMemo(
    () => [
      {
        accessorKey: "student_id",
        header: translations.labels.studentId || "Student ID",
        size: 100,
      },
      {
        accessorKey: "name",
        header: translations.labels.name || "Name",
        size: 150,
      },
      {
        accessorKey: "email",
        header: translations.labels.email || "Email",
        size: 200,
      },
      {
        accessorKey: "class",
        header: translations.labels.classLabel || "Class",
        size: 100,
      },
      {
        accessorKey: "student_section",
        header: translations.labels.sectionLabel || "Section",
        size: 100,
      },
      {
        accessorKey: "blood_group",
        header: translations.labels.bloodGroup || "Blood Group",
        size: 120,
      },
      {
        accessorKey: "medical_condition",
        header: translations.labels.medicalCondition || "Medical Condition",
        size: 180,
      },
      {
        accessorKey: "action",
        header: translations.labels.actions || "Actions",
        size: 80,
        Cell: ({ row }) => (
          <div className={styles.actionButtons}>
            <button
              className={styles.viewButton}
              onClick={() => handleView(row.original)}
              title={translations.labels.view || "View"}
            >
              <FaEye />
            </button>
          </div>
        ),
      },
    ],
    [translations],
  );

  // ─── Get columns based on category ───
  const getColumns = () => {
    const category = getCategoryValue();
    switch (category) {
      case "Staff":
        return staffColumns;
      case "Student":
        return studentColumns;
      default:
        return studentColumns;
    }
  };

  // ─── Get table title ───
  const getTableTitle = () => {
    const category = getCategoryValue();
    if (category === "Staff")
      return translations.labels.staffDefectList || "Staff Defect List";
    if (category === "Student")
      return translations.labels.studentDefectList || "Student Defect List";
    return translations.labels.defectList || "Defect List";
  };

  // ─── Modal Handlers ───
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  const handleModalClose = () =>
    setModalState({ open: false, type: "", message: "", redirectPath: "" });

  // ─── Show list section only for Staff / Student ───
  const categoryVal = getCategoryValue();
  const showList = categoryVal === "Staff" || categoryVal === "Student";

  // ─── Compute pageCount from API response ───
  const pageCount = Math.ceil(totalRows / pagination.pageSize) || 0;

  return (
    <>
      {/* Upload Section */}
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaTable />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels.bulkUpload || "Bulk Upload"}
          </h1>
        </div>

        <div className={styles.uploadForm}>
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels.category || "Category"}
                name="category"
                value={formData.category}
                onChange={(value) => handleDropdownChange("category", value)}
                options={categoryOptions}
                placeholder={
                  translations.labels.selectCategory || "Select Category"
                }
                required
              />
            </div>
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomFileInput
                label={
                  translations.labels.uploadYourFile || "Upload your file here"
                }
                name="file"
                value={formData.file}
                onChange={handleFileChange}
                accept=".xlsx,.xls,.csv"
                placeholder={
                  formData.original_name
                    ? formData.original_name
                    : "E.g. file.xlsx"
                }
                buttonText={translations.labels.chooseFile || "Choose File"}
                disabled={attendanceUploading}
              />
              {attendanceUploading && (
                <span className={styles.uploadingHint}>
                  {translations.labels.uploading || "Uploading..."}
                </span>
              )}
            </div>
          </div>

          <div className={styles.uploadActions}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.downloadButton}
              fullWidth={false}
              onClick={handleDownloadFrameSet}
              disabled={downloading}
            >
              <FiDownload style={{ marginRight: "8px" }} />
              {downloading
                ? translations.labels.loading || "Loading..."
                : translations.labels.downloadFrameSet ||
                  "DOWNLOAD THE FRAME SET"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={styles.importButton}
              fullWidth={false}
              onClick={handleUploadFrameSet}
              disabled={uploading || attendanceUploading}
            >
              <FiUpload style={{ marginRight: "8px" }} />
              {uploading
                ? translations.labels.uploading || "Uploading..."
                : translations.labels.uploadFrameSet || "UPLOAD FRAME SET"}
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Defect List Section — only for Staff & Student */}
      {showList && (
        <div className={`${styles.container} mt-4`}>
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaTable />
            </div>
            <h1 className={styles.headerTitle}>{getTableTitle()}</h1>
          </div>

          <CustomMRTTable
            columns={getColumns()}
            data={data}
            loading={loading}
            pagination={pagination}
            placeholder={translations.labels.searchButton || "Search"}
            rowCount={totalRows}
            pageCount={pageCount}
            manualPagination={true}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onSearchEnter={(value) => {
              setApiSearch(value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        </div>
      )}

      {/* Popup Modal */}
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? translations.labels.success || "Success"
            : translations.labels.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />
    </>
  );
};

export default BulkUpload;
